/**
 * Computes the viewport coordinates for a dropdown menu anchored to a
 * trigger button. Assumes the menu will be rendered with
 * `position: fixed` (e.g. via a React portal to document.body) so the
 * returned values are viewport-relative, not parent-relative.
 *
 * Why this is a pure function:
 *   - The B8 clipping bug shipped because the avatar dropdown relied on
 *     `position: absolute; right: 0` inside the 220px sidebar. `right: 0`
 *     aligns the dropdown's right edge with the button's right edge; with
 *     a 220px menu and a button near the sidebar's right, the dropdown's
 *     left edge projects ~16px OUTSIDE the sidebar boundary and clips
 *     against an ancestor with `overflow: hidden`.
 *   - Moving to a portal + fixed positioning escapes every ancestor. The
 *     remaining concern is keeping the menu inside the VIEWPORT — if the
 *     button is near the right edge, naively using left=buttonLeft would
 *     overflow. This function keeps the menu fully on-screen by clamping
 *     to a configurable margin.
 *
 * Testing this without a layout engine:
 *   - Input is a plain rect + width/height/viewport numbers.
 *   - Output is plain numbers.
 *   - `node --test` verifies the logic; no JSDOM / Playwright needed.
 */

export interface Rect {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

export interface AnchorInput {
  /** Button bounding box in viewport coordinates. */
  trigger: Rect;
  /** Menu width in pixels. */
  menuWidth: number;
  /** Menu height in pixels (used for flip-above-if-no-room logic). */
  menuHeight: number;
  /** Viewport width in pixels. */
  viewportWidth: number;
  /** Viewport height in pixels. */
  viewportHeight: number;
  /** Pixel gap between the trigger and the menu. Default 6. */
  gap?: number;
  /** Minimum pixels to keep between menu edges and viewport edges. Default 8. */
  edgePadding?: number;
}

export interface AnchorOutput {
  /** Pixel value for the menu's `top` CSS (viewport coords). */
  top: number;
  /** Pixel value for the menu's `left` CSS (viewport coords). */
  left: number;
  /** Whether we flipped above the trigger because there wasn't room below. */
  flippedAbove: boolean;
}

/**
 * Right-aligned menu anchor (menu's right edge lines up with trigger's
 * right edge, i.e. the behaviour operators expect from an avatar button
 * in the top-right corner of the header). Falls back to:
 *   - left-aligned when right-aligned would clip the viewport left edge
 *   - flipped above when there isn't enough room below
 */
export function computeDropdownAnchor(input: AnchorInput): AnchorOutput {
  const gap = input.gap ?? 6;
  const edge = input.edgePadding ?? 8;

  // Horizontal: prefer right-aligned. Compute where the menu's left edge
  // lands when anchored to the trigger's right edge.
  let left = input.trigger.right - input.menuWidth;

  // If that pushes the left edge off-screen, fall back to left-aligned
  // (menu's left edge = trigger's left edge).
  if (left < edge) {
    left = input.trigger.left;
  }

  // Final clamp so we never overflow either viewport edge. If the menu is
  // somehow wider than the viewport, keep it flush to the left edge.
  const maxLeft = input.viewportWidth - input.menuWidth - edge;
  if (left > maxLeft) left = Math.max(edge, maxLeft);
  if (left < edge) left = edge;

  // Vertical: prefer below the trigger. Flip above if there's not enough
  // room below (accounting for the menu height + edge padding).
  const spaceBelow = input.viewportHeight - input.trigger.bottom - edge;
  const spaceAbove = input.trigger.top - edge;
  let top = input.trigger.bottom + gap;
  let flippedAbove = false;
  if (spaceBelow < input.menuHeight && spaceAbove >= input.menuHeight) {
    top = input.trigger.top - gap - input.menuHeight;
    flippedAbove = true;
  }

  return { top, left, flippedAbove };
}
